import { nextBusinessDay, todayInSaoPaulo } from "./dates";
import { DomainError } from "./errors";
import { GNRE_BARCODE_SEGMENT, parseCollectionBarcode, type ParsedCollectionBarcode } from "./gnre";

// ===== Espaço Meu Contador, arquivos fiscais e DAS MEI (T3.5, RF-CTB-01/02, RF-PRF-10) =====

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

// ---------- competência ----------

const COMPETENCE_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Primeiro e último dia (AAAA-MM-DD) da competência AAAA-MM. */
export function competenceRange(competence: string): { start: string; end: string } {
  const m = COMPETENCE_RE.exec(competence) ?? fail("validation", "Competência inválida (use AAAA-MM).");
  const y = Number(m![1]);
  const mo = Number(m![2]);
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return { start: `${m![1]}-${m![2]}-01`, end: `${m![1]}-${m![2]}-${String(last).padStart(2, "0")}` };
}

export function shiftCompetence(competence: string, months: number): string {
  const { start } = competenceRange(competence);
  const total = Number(start.slice(0, 4)) * 12 + Number(start.slice(5, 7)) - 1 + months;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
}

/** Competência de hoje (São Paulo). */
export const currentCompetence = (today: string = todayInSaoPaulo()): string => today.slice(0, 7);

/** Competência que já começou e de 2023 em diante (arquivos fiscais e DAS). */
export function assertPastOrCurrentCompetence(competence: string, today: string = todayInSaoPaulo()): void {
  competenceRange(competence);
  if (competence > currentCompetence(today)) fail("validation", "A competência ainda não começou.");
  if (competence < "2023-01") fail("validation", "Competência anterior a 2023 não é gerada pelo ERP.");
}

// ---------- arquivos fiscais e pacote ----------

export const FISCAL_FILE_KINDS = ["sped_fiscal", "sped_contribuicoes", "sintegra"] as const;
export type FiscalFileKind = (typeof FISCAL_FILE_KINDS)[number];

export const FISCAL_FILE_LABELS: Readonly<Record<FiscalFileKind, string>> = {
  sped_fiscal: "SPED Fiscal (EFD ICMS/IPI)",
  sped_contribuicoes: "SPED Contribuições (EFD PIS/COFINS)",
  sintegra: "Sintegra",
};

/** Nome do arquivo: `SPED-FISCAL-12345678000195-092026.txt`. */
export function fiscalFileName(kind: FiscalFileKind, document: string, competence: string): string {
  const prefix = {
    sped_fiscal: "SPED-FISCAL",
    sped_contribuicoes: "SPED-CONTRIBUICOES",
    sintegra: "SINTEGRA",
  }[kind];
  const doc = document.replace(/\D/g, "") || "SEM-CNPJ";
  return `${prefix}-${doc}-${competence.slice(5, 7)}${competence.slice(0, 4)}.txt`;
}

/** Conteúdo possível do pacote do contador. */
export const PACKAGE_CONTENTS = [
  "nfe_xml",
  "nfce_xml",
  "nfse_xml",
  "inbound_xml",
  "sped_fiscal",
  "sped_contribuicoes",
  "sintegra",
  "financial_report",
] as const;
export type PackageContent = (typeof PACKAGE_CONTENTS)[number];

export const PACKAGE_CONTENT_LABELS: Readonly<Record<PackageContent, string>> = {
  nfe_xml: "XML das NF-e",
  nfce_xml: "XML das NFC-e",
  nfse_xml: "XML das NFS-e",
  inbound_xml: "XML das notas de entrada",
  sped_fiscal: "SPED Fiscal",
  sped_contribuicoes: "SPED Contribuições",
  sintegra: "Sintegra",
  financial_report: "Relatórios financeiros (CSV)",
};

export const DEFAULT_PACKAGE_CONTENTS: readonly PackageContent[] = [
  "nfe_xml",
  "nfce_xml",
  "nfse_xml",
  "inbound_xml",
  "sped_fiscal",
  "sintegra",
  "financial_report",
];

/** Conteúdo sem repetição, na ordem canônica. */
export const normalizeContents = (c: readonly string[]): PackageContent[] =>
  PACKAGE_CONTENTS.filter((x) => c.includes(x));

/** ZIP anexo até 10 MB; acima disso o e-mail só avisa (o contador baixa pelo usuário dele). */
export const PACKAGE_ATTACH_MAX_BYTES = 10 * 1024 * 1024;

/** Nome do pacote: `contador-092026-n3.zip`. */
export const packageFileName = (competence: string, number: number) =>
  `contador-${competence.slice(5, 7)}${competence.slice(0, 4)}-n${number}.zip`;

/**
 * Próximo envio automático: no dia `day` deste mês se ainda não passou (e a competência anterior não foi enviada);
 * senão no do mês seguinte. null = desligado.
 */
export function nextAutoRun(day: number | null, today: string, lastCompetence: string | null): string | null {
  if (day === null) return null;
  if (!Number.isInteger(day) || day < 1 || day > 28)
    fail("validation", "Dia do envio automático entre 1 e 28.");
  const thisMonth = `${today.slice(0, 7)}-${String(day).padStart(2, "0")}`;
  const due = shiftCompetence(today.slice(0, 7), -1);
  if (thisMonth >= today && lastCompetence !== due) return thisMonth;
  return `${shiftCompetence(today.slice(0, 7), 1)}-${String(day).padStart(2, "0")}`;
}

// ---------- DAS MEI ----------

export const MEI_ACTIVITIES = ["commerce", "industry", "services", "commerce_services"] as const;
export type MeiActivity = (typeof MEI_ACTIVITIES)[number];
export const MEI_ACTIVITY_LABELS: Readonly<Record<MeiActivity, string>> = {
  commerce: "Comércio",
  industry: "Indústria",
  services: "Serviços",
  commerce_services: "Comércio e serviços",
};

/** Salário mínimo nacional por ano (centavos): 2024 R$ 1.412; 2025 R$ 1.518; 2026 R$ 1.621 (Decreto 12.797/2025). */
export const MINIMUM_WAGE_CENTS: Readonly<Record<number, number>> = {
  2024: 141_200,
  2025: 151_800,
  2026: 162_100,
};
/** INSS do MEI: 5% do salário mínimo (pct4). */
export const MEI_INSS_PCT4 = 50_000;
export const MEI_ICMS_CENTS = 100;
export const MEI_ISS_CENTS = 500;

/** Link do PGMEI (emissão oficial do DAS, sem API pública). */
export const PGMEI_URL =
  "https://www8.receita.fazenda.gov.br/SimplesNacional/Aplicacoes/ATSPO/pgmei.app/Identificacao";

export type DasMeiAmounts = { inssCents: number; icmsCents: number; issCents: number; totalCents: number };

/** Valor do DAS da competência pelos valores fixos do ano (sem multa e juros: atraso só pelo PGMEI). */
export function dasMeiAmounts(competence: string, activity: MeiActivity): DasMeiAmounts {
  competenceRange(competence);
  const year = Number(competence.slice(0, 4));
  const wage =
    MINIMUM_WAGE_CENTS[year] ??
    fail(
      "das_values_unknown",
      `Valores do DAS MEI de ${year} ainda não cadastrados no ERP. Confira no PGMEI.`,
    );
  const inss = Number((BigInt(wage!) * BigInt(MEI_INSS_PCT4) + 500_000n) / 1_000_000n);
  if (!MEI_ACTIVITIES.includes(activity)) fail("validation", "Atividade do MEI inválida.");
  const icms = activity === "services" ? 0 : MEI_ICMS_CENTS;
  const iss = activity === "services" || activity === "commerce_services" ? MEI_ISS_CENTS : 0;
  return { inssCents: inss, icmsCents: icms, issCents: iss, totalCents: inss + icms + iss };
}

/** Vencimento: dia 20 do mês seguinte; sem expediente bancário, o próximo dia útil (LC 123, art. 21, III). */
export function dasMeiDueDate(competence: string): string {
  return nextBusinessDay(`${shiftCompetence(competence, 1)}-20`);
}

/** Guia vencida (o PGMEI calcula multa e juros: emita de novo lá). */
export const dasMeiOverdue = (dueDate: string, today: string = todayInSaoPaulo()): boolean => today > dueDate;

/** Código da guia do PGMEI: arrecadação, segmento de órgão governamental e valor igual ao do DAS. */
export function checkDasBarcode(input: string, amountCents: number): ParsedCollectionBarcode {
  const parsed = parseCollectionBarcode(input);
  if (parsed.barcode[1] !== GNRE_BARCODE_SEGMENT)
    fail(
      "invalid_barcode",
      "Não é guia de tributo: o 2º dígito (segmento) do DAS é 5 (órgãos governamentais).",
    );
  if (parsed.amountCents === null || parsed.amountCents !== amountCents)
    fail(
      "barcode_amount_mismatch",
      "O valor do código de barras é diferente do valor do DAS. Confira a guia emitida no PGMEI.",
    );
  return parsed;
}

/** MEI: CRT 4 (NT 2024.001) ou regime "mei" no cadastro da empresa. */
export const isMeiCompany = (crt: number | null | undefined, taxRegime: string | null | undefined): boolean =>
  crt === 4 || taxRegime === "mei";
